import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

type Client = { id: string; name: string | null; business_name: string | null; public_slug: string | null };
type Post = {
  id: string;
  title: string | null;
  slug: string | null;
  content: string | null;
  meta_description: string | null;
  featured_image: string | null;
  published_at: string | null;
};

export default function SiteBlogArticle() {
  const { clientSlug, slug } = useParams();
  const [client, setClient] = useState<Client | null>(null);
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!clientSlug || !slug) return;
      const { data: c } = await supabase
        .from("clients")
        .select("id,name,business_name,public_slug")
        .eq("public_slug", clientSlug)
        .maybeSingle();
      if (!c) {
        setLoading(false);
        return;
      }
      setClient(c as Client);
      const { data: p } = await supabase
        .from("blog_posts")
        .select("id,title,slug,content,meta_description,featured_image,published_at")
        .eq("client_id", c.id)
        .eq("published", true)
        .eq("slug", slug)
        .maybeSingle();
      setPost((p ?? null) as Post | null);
      setLoading(false);
    })();
  }, [clientSlug, slug]);

  useEffect(() => {
    if (!post) return;
    document.title = post.title ?? "Article";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    if (post.meta_description) setMeta("description", post.meta_description);
    // og tags
    const setOg = (prop: string, content: string) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", prop);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    if (post.title) setOg("og:title", post.title);
    if (post.meta_description) setOg("og:description", post.meta_description);
    if (post.featured_image) setOg("og:image", post.featured_image);
    setOg("og:type", "article");
  }, [post]);

  if (loading) return <div className="p-8">Loading…</div>;
  if (!post) return <div className="p-8">Not found.</div>;

  return (
    <article className="mx-auto max-w-3xl p-8 prose prose-neutral dark:prose-invert">
      <nav className="mb-6 text-sm">
        <Link to={`/sites/${clientSlug}/blog`} className="text-muted-foreground hover:underline">
          ← {client?.business_name || client?.name || "Blog"}
        </Link>
      </nav>
      <h1>{post.title}</h1>
      {post.published_at && (
        <time className="text-sm text-muted-foreground block mb-6">
          {new Date(post.published_at).toLocaleDateString()}
        </time>
      )}
      {post.featured_image && (
        <img src={post.featured_image} alt={post.title ?? ""} className="w-full rounded my-6" />
      )}
      <div dangerouslySetInnerHTML={{ __html: post.content ?? "" }} />
    </article>
  );
}
